import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";

export type BnButtonVariant = "primary" | "secondary" | "danger" | "ghost";
export type BnButtonSize = "sm" | "md";

/**
 * bn-button is the primary action control. It renders a native <button> in the
 * shadow root and projects its label via <slot>. Variants map to the DS brand
 * (celeste/teal primary), a neutral secondary, a destructive danger, and a
 * chromeless ghost. Use `loading` to show an inline spinner and block clicks —
 * a publish, a provision or a proxied CLI call is a job (`SC-F332`), so the
 * button that starts one stays busy until the poll resolves.
 *
 * Properties:
 * - `variant`  — `primary` | `secondary` (default) | `danger` | `ghost`
 * - `size`     — `sm` | `md` (default)
 * - `type`     — the native button type: `button` (default) | `submit` | `reset`
 * - `disabled` — blocks interaction and dims the control
 * - `loading`  — shows an inline spinner and swallows clicks
 * - `block`    — full-width; reflected, so `:host([block])` styles it whether
 *                the page sets the attribute or the property
 *
 * Events: none of its own. Clicks reach the page as the native `click` event,
 * except while `disabled` or `loading`, when they are stopped at the boundary.
 *
 *   <bn-button variant="primary" @click=${this.publish}>Publish</bn-button>
 *   <bn-button variant="danger" size="sm" ?loading=${busy}>Delete instance</bn-button>
 */
@customElement("bn-button")
export class BnButton extends LitElement {
  @property() variant: BnButtonVariant = "secondary";
  @property() size: BnButtonSize = "md";
  @property() type: "button" | "submit" | "reset" = "button";
  @property({ type: Boolean }) disabled = false;
  @property({ type: Boolean }) loading = false;
  /** Renders the button at full container width. Reflected for `:host([block])`. */
  @property({ type: Boolean, reflect: true }) block = false;

  static override styles = css`
    :host {
      display: inline-block;
    }
    :host([block]) {
      display: block;
    }
    button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: var(--bino-space-xs);
      width: 100%;
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-md);
      font-weight: 600;
      line-height: 1;
      padding: 0.5rem 0.8125rem;
      border-radius: var(--bino-radius);
      border: 1px solid var(--bino-border-light);
      background: var(--bino-surface);
      color: var(--bino-text);
      cursor: pointer;
      user-select: none;
      transition:
        background var(--bino-transition-fast),
        border-color var(--bino-transition-fast),
        box-shadow var(--bino-transition-fast);
    }
    button.sm {
      font-size: var(--bino-font-size-sm);
      padding: 0.3125rem 0.625rem;
    }
    button:hover:not(:disabled) {
      border-color: var(--bino-border-hover);
      background: var(--bino-surface-hover);
    }
    button:focus-visible {
      outline: none;
      border-color: var(--bino-primary);
      box-shadow: 0 0 0 3px var(--bino-focus-ring);
    }
    button:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }

    button.primary {
      background: var(--bino-primary);
      border-color: var(--bino-primary);
      color: var(--bino-on-primary);
    }
    button.primary:hover:not(:disabled) {
      background: var(--bino-primary-hover);
      border-color: var(--bino-primary-hover);
    }
    button.danger {
      background: var(--bino-surface);
      border-color: var(--bino-error-border);
      color: var(--bino-error);
    }
    button.danger:hover:not(:disabled) {
      background: var(--bino-error-bg);
      border-color: var(--bino-error);
    }
    button.ghost {
      background: transparent;
      border-color: transparent;
      color: var(--bino-text-secondary);
    }
    button.ghost:hover:not(:disabled) {
      background: var(--bino-surface-hover);
      color: var(--bino-text);
    }

    .spinner {
      width: 0.875em;
      height: 0.875em;
      border: 2px solid currentColor;
      border-right-color: transparent;
      border-radius: 50%;
      animation: spin 0.6s linear infinite;
    }
    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }
    /* The spinner is a busy indicator, not decoration: under reduced motion it
       slows rather than stops, so "still working" stays legible. */
    @media (prefers-reduced-motion: reduce) {
      .spinner {
        animation-duration: 1.2s;
      }
    }
  `;

  private onClick(e: Event) {
    if (this.disabled || this.loading) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  }

  override render() {
    const cls = `${this.variant} ${this.size === "sm" ? "sm" : ""}`;
    return html`
      <button
        class=${cls}
        type=${this.type}
        ?disabled=${this.disabled || this.loading}
        aria-busy=${this.loading ? "true" : "false"}
        @click=${this.onClick}
      >
        ${this.loading ? html`<span class="spinner" aria-hidden="true"></span>` : ""}
        <slot></slot>
      </button>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bn-button": BnButton;
  }
}
